import type { FC } from "react"
import React, { useMemo } from "react"
import {
  Link,
  Stack,
  Flex,
  Text,
  Icon,
  Skeleton,
  Button,
} from "@chakra-ui/react"
import truncate from "truncate"
import BigNumber from "bignumber.js"

import { useAllGovernanceModuleInformations } from "governance/components/ContractsModal"
import type { AccountId, Organization } from "query/graphql"
import {
  GovernorType,
  useGovernanceDelegatesSummaryQuery,
  useOrganizationDelegatesSummaryQuery,
  useTokenQuery,
  useTokensQuery,
} from "query/graphql"
import { getWeightLabel, stringToBigNumber } from "common/helpers/bignumber"
import { ROUTES } from "common/constants/routes"
import ArrowRight from "ui/components/icons/ArrowRight"
import CardContainer from "common/components/CardContainer"
import {
  FeatureFlag,
  useOrganization,
} from "organization/providers/OrganizationProvider"
import { isMultiChainTokenIds } from "organization/helpers/organization"

type Props = {
  governorIds: AccountId[]
  governorId?: AccountId
  organization: Organization
}
const GovernanceDelegatesSummary: FC<Props> = ({
  governorIds,
  governorId,
  organization,
}) => {
  const tokenId =
    organization && organization.tokenIds?.length > 0
      ? organization.tokenIds[0]
      : undefined
  const { slug } = organization
  const { isFeatureFlagOn } = useOrganization()

  const hideDelegatedTokensCount = isFeatureFlagOn(
    FeatureFlag.HideDelegatedTokensCount,
  )

  const { governanceModules, isLoading: isGovernanceModulesLoading } =
    useAllGovernanceModuleInformations({
      governanceIds: governorIds,
    })

  const { data: govData, isFetching: isGovLoading } =
    useGovernanceDelegatesSummaryQuery(
      {
        input: { id: governorId ?? "" },
      },
      { enabled: Boolean(governorId) },
    )

  const { data: orgData, isFetching: isOrgLoading } =
    useOrganizationDelegatesSummaryQuery({
      input: { id: organization.id },
    })

  const { data: tokenData } = useTokenQuery(
    {
      input: { id: tokenId ?? "" },
    },
    { enabled: Boolean(tokenId) },
  )

  const getOrgStats = () => {
    if (!orgData?.organization || !tokenData?.token) {
      return {
        delegatedTokens: "-",
      }
    }

    const { organization } = orgData
    const decimals = tokenData.token.decimals

    const delegatedTokens =
      organization.tokenIds &&
      organization.tokenIds.length > 0 &&
      organization?.delegatesVotesCount
        ? getWeightLabel(organization.delegatesVotesCount, decimals)
        : "Indexing..."

    return { delegatedTokens }
  }

  const getGovStats = () => {
    // If no governor is provided, just return a basic "-" value
    if (!govData?.governor) {
      return {
        quorum: null,
        proposalThreshold: null,
      }
    }

    const { governor } = govData
    const { decimals } = governor?.token

    const quorum = getWeightLabel(governor.quorum, decimals)

    const proposalThreshold =
      governor?.parameters?.proposalThreshold &&
      typeof decimals !== "undefined" &&
      decimals >= 0
        ? getWeightLabel(governor.parameters.proposalThreshold, decimals)
        : ""

    return { quorum, proposalThreshold }
  }

  const { data: tokensData } = useTokensQuery({
    input: {
      ids: organization.tokenIds,
    },
  })

  const tokenSupply = useMemo(() => {
    if (!tokenId || !tokensData) {
      return "0"
    }

    const { tokens } = tokensData

    const total = tokens.reduce((acc, token) => {
      const supply = new BigNumber(token.supply)

      return acc.plus(supply)
    }, new BigNumber(0))

    return getWeightLabel(total.toFixed(), tokens[0].decimals)
  }, [tokenId, tokensData])

  const { delegatedTokens } = getOrgStats()
  const { proposalThreshold } = getGovStats()

  const getNotAave = () => {
    if (!govData?.governor) return true

    const { governor: governorData } = govData
    const { contracts } = governorData
    const { governor } = contracts
    const { type } = governor

    return type !== GovernorType.Aave
  }

  const notAave = getNotAave()

  const isLoadingData = (isGovLoading && Boolean(governorId)) || isOrgLoading

  const isMultiChain = isMultiChainTokenIds(organization.tokenIds)

  return (
    <>
      <CardContainer pt={0} title="Details">
        <Stack direction="column" pt={{ base: 4, md: 0 }} spacing={4}>
          {tokenId ? (
            <Flex align="center" justify="space-between" w="full">
              {isLoadingData ? (
                <Skeleton h="21px" w="full" />
              ) : (
                <>
                  <Text color="gray.600" fontSize="14px" fontWeight={500}>
                    Total Supply
                  </Text>
                  <Text color="gray.600" fontSize="14px">
                    {tokenSupply}
                  </Text>
                </>
              )}
            </Flex>
          ) : null}

          {hideDelegatedTokensCount ? (
            <></>
          ) : (
            <Flex align="center" justify="space-between" w="full">
              {isLoadingData ? (
                <Skeleton h="21px" w="full" />
              ) : (
                <>
                  <Text color="gray.600" fontSize="14px" fontWeight={500}>
                    Delegated Tokens
                  </Text>
                  <Text color="gray.600" fontSize="14px">
                    {delegatedTokens}
                  </Text>
                </>
              )}
            </Flex>
          )}

          {governanceModules ? (
            <Stack spacing={4} w="full">
              {isGovernanceModulesLoading
                ? governorIds
                    .slice(0, 2)
                    .map((id) => <Skeleton key={id} h="21px" w="full" />)
                : governanceModules
                    .filter(({ governor }) =>
                      [
                        GovernorType.Governoralpha,
                        GovernorType.Governorbravo,
                        GovernorType.Openzeppelingovernor,
                        GovernorType.Hub,
                      ].includes(governor.type),
                    )
                    .map((governanceModule) => {
                      const bigQuorum = stringToBigNumber(
                        governanceModule.governor.quorum,
                      )
                      const quorumLabel = getWeightLabel(
                        bigQuorum,
                        governanceModule.governor.token.decimals,
                      )

                      return (
                        <React.Fragment key={governanceModule.governor.id}>
                          <Flex justify="space-between">
                            <Text
                              color="gray.600"
                              fontSize="14px"
                              fontWeight={500}
                            >
                              {governorIds.length > 1
                                ? `Quorum (${truncate(
                                    governanceModule.governor.name,
                                    17,
                                  )})`
                                : `Quorum`}
                            </Text>
                            <Text color="gray.600" fontSize="14px">
                              {quorumLabel}
                            </Text>
                          </Flex>
                        </React.Fragment>
                      )
                    })}
            </Stack>
          ) : null}

          {notAave ? (
            governorId && !isMultiChain && governorIds.length <= 1 ? (
              <Flex align="center" justify="space-between" w="full">
                {isLoadingData ? (
                  <Skeleton h="21px" w="full" />
                ) : (
                  <>
                    <Text color="gray.600" fontSize="14px" fontWeight={500}>
                      Proposal Threshold
                    </Text>
                    <Text color="gray.600" fontSize="14px">
                      {proposalThreshold}
                    </Text>
                  </>
                )}
              </Flex>
            ) : null
          ) : (
            <Flex align="center" justify="space-between" w="full">
              {isLoadingData ? (
                <Skeleton h="21px" w="full" />
              ) : (
                <>
                  <Link
                    className="no-underline no-box-shadow"
                    href={ROUTES.governance.id(slug)}
                  >
                    {" "}
                    <Button
                      color="gray.800"
                      rightIcon={
                        <Icon as={ArrowRight} fill="gray.600" h={2} w={2} />
                      }
                      variant="link"
                    >
                      For all parameters, visit AAVE DAO page
                    </Button>
                  </Link>
                </>
              )}
            </Flex>
          )}
        </Stack>
      </CardContainer>
    </>
  )
}

export default GovernanceDelegatesSummary
